import { defineCollection } from "@rebasepro/common";
/**
 * Pages that answer this category's questions on someone else's site.
 *
 * ── The inversion this board is built on ─────────────────────────────────────
 * Signals scores a post by **buyer intent**: fresh, a question, someone stuck
 * right now, reachable in the same thread. `relevance.ts` is tuned hard for
 * that — a "Top 10 Supabase alternatives" listicle is collapsed to 1 by the
 * anti-signal table, and a post older than a week is discarded by the
 * qualification bar in ICP.md.
 *
 * Those are the right rules for outreach and exactly the wrong rules here.
 * The page that decides an evaluation is almost never this week's question. It
 * is the four-year-old thread that still ranks first, still collects a comment
 * a month, and is what a model quotes when someone asks it what to use. Every
 * property that makes a post worthless as a lead — old, answered, a listicle,
 * a comparison, dominated by opinions — is what makes it valuable as a citation
 * target.
 *
 * So this board runs `relevance.ts` backwards. Same eight sources, same rows
 * coming off the same nightly scrape, opposite objective function.
 *
 * ── Two intakes, and the difference matters ──────────────────────────────────
 *  - **cited** — the model actually quoted this URL when asked a buyer
 *    question. Not an inference: it came back in the answer's own grounding
 *    metadata, with the question that surfaced it recorded on the row.
 *  - **category** — nobody has cited it yet, but it is the shape of page that
 *    gets cited: a comparison, an "alternatives to" thread, a migration
 *    write-up. A bet, and labelled as one.
 *
 * `times_cited` separates them at a glance, and sorting by it is the closest
 * thing this app has to a priority list.
 *
 * ── The rule that does not change ────────────────────────────────────────────
 * **The agent still posts nothing.** A row here is a page to go and read, and
 * at most a draft of a comment a person may choose to write. The hard rule in
 * ICP.md covers this board exactly as it covers Signals, and it matters more
 * here: a four-year-old thread with a hundred participants is the single worst
 * place for something to arrive that reads as automated.
 */
/** A value as a trimmed string, or "" — display roles are text. */
const str = (value) => (typeof value === "string" ? value.trim() : value == null ? "" : String(value));
const citationTargetsCollection = defineCollection({
    name: "Citation targets",
    singularName: "Citation target",
    slug: "citation_targets",
    table: "citation_targets",
    description: "Pages that answer this category's questions somewhere else. Sorted by how often an AI actually quoted them. "
        + "Read one, decide whether a genuinely useful comment belongs there — the agent will never write it for you.",
    history: true,
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        url: {
            name: "URL",
            type: "string",
            validation: { required: true, unique: true },
            url: true,
            description: "The dedup key. One row per page, however many questions surfaced it."
        },
        title: {
            name: "Title",
            type: "string",
            description: "The page's own title where we could read one, otherwise the domain"
        },
        domain: {
            name: "Domain",
            type: "string",
            description: "Bare host. Sort by this to see which sites own the category's answers — usually four or five do."
        },
        kind: {
            name: "How we found it",
            type: "string",
            validation: { required: true },
            defaultValue: "category",
            enum: [
                { id: "cited", label: "An AI cited it", color: "green" },
                { id: "category", label: "Looks like a page that gets cited", color: "blue" }
            ],
            description: "Evidence versus inference. Never blur these — one is a fact about a model's answer, the other is a guess about page shape."
        },
        times_cited: {
            name: "Times cited",
            type: "number",
            defaultValue: 0,
            description: "How many distinct question-and-run pairs have quoted this page. The priority number on this board."
        },
        prompts: {
            name: "Questions it answered",
            type: "array",
            columnType: "text[]",
            of: { name: "Question", type: "string" },
            description: "Which buyer questions surfaced this page. A page cited for three different questions is load-bearing for the whole category."
        },
        source: {
            name: "Source",
            type: "string",
            enum: [
                { id: "hn", label: "Hacker News", color: "orange" },
                { id: "reddit", label: "Reddit", color: "red" },
                { id: "github", label: "GitHub", color: "gray" },
                { id: "github_discussions", label: "GitHub Discussions", color: "grayDark" },
                { id: "stackoverflow", label: "Stack Overflow", color: "yellow" },
                { id: "devto", label: "dev.to", color: "purple" },
                { id: "mastodon", label: "Mastodon", color: "blue" },
                { id: "lobsters", label: "Lobste.rs", color: "redLight" },
                { id: "web", label: "Open web", color: "greenLight" },
                { id: "other", label: "Other", color: "blueLight" }
            ]
        },
        score: {
            name: "Citation value",
            type: "number",
            validation: { min: 1, max: 5 },
            description: "1-5, scored by the INVERSE of the lead scorer: comparison shapes and category questions score high, a fresh cry for help scores low."
        },
        score_reason: {
            name: "Why this score",
            type: "string",
            admin: { markdown: true },
            description: "What matched. Says out loud when a page scored well here precisely because it would score badly as a lead."
        },
        quote: {
            name: "What it says about us",
            type: "string",
            admin: { markdown: true },
            description: "Where the page names rebase or a rival, verbatim. Empty when nobody has mentioned anyone yet — which is its own kind of opportunity."
        },
        posted_at: {
            name: "Published",
            type: "date",
            description: "When the page went up. Deliberately NOT a freshness filter here: age is evidence the page endures."
        },
        status: {
            name: "Status",
            type: "string",
            validation: { required: true },
            defaultValue: "new",
            enum: [
                { id: "new", label: "New", color: "blue" },
                { id: "shortlisted", label: "Worth answering", color: "purple" },
                { id: "covered", label: "Covered", color: "green" },
                { id: "skipped", label: "Skipped", color: "gray" }
            ]
        },
        first_seen: {
            name: "First seen",
            type: "date",
            autoValue: "on_create",
            admin: { readOnly: true }
        },
        last_seen: {
            name: "Last seen",
            type: "date",
            description: "Updated every time a run finds it again. A target that stops appearing has stopped mattering."
        },
        __order: {
            name: "Order",
            type: "string",
            admin: { disabled: true, hideFromCollection: true }
        }
    },
    admin: {
        icon: "FormatQuote",
        group: "Visibility",
        defaultViewMode: "list",
        enabledViews: ["list", "table", "kanban"],
        kanban: {
            columnProperty: "status"
        },
        orderProperty: "__order",
        /**
         * `times_cited` is the evidence and the sort key, so it belongs on the
         * row: a page an assistant has quoted twelve times is a different
         * proposition from one nobody has quoted, and the list was sorted by a
         * number it did not show.
         */
        display: {
            title: "title",
            subtitle: ({ entity }) => str(entity.values.quote) || str(entity.values.score_reason) || str(entity.values.domain),
            status: "status",
            date: "last_seen",
            tags: ({ entity }) => [
                str(entity.values.domain),
                entity.values.times_cited ? `cited ${entity.values.times_cited}x` : "",
                entity.values.score ? `score ${entity.values.score}` : ""
            ].filter(Boolean)
        },
        sort: ["times_cited", "desc"],
        propertiesOrder: [
            "title",
            "status",
            "kind",
            "times_cited",
            "score",
            "domain",
            "url",
            "source",
            "prompts",
            "score_reason",
            "quote",
            "posted_at",
            "first_seen",
            "last_seen",
            "__order"
        ],
        filterPresets: [
            {
                label: "An AI actually cited it",
                filterValues: { kind: ["==", "cited"] }
            },
            {
                label: "Untouched",
                filterValues: { status: ["==", "new"] }
            },
            {
                label: "Strongest bets",
                filterValues: { score: [">=", 4] }
            }
        ]
    }
});
export default citationTargetsCollection;
